<template>
  <VisionModal
    :open="showModal"
    @close="dismiss"
  >
    <div :class="bem('inner')">
      <BaseHeading
        tag="h3"
        size="h2"
        :class="bem('title')"
      >
        Are you in the right place?
      </BaseHeading>

      <p :class="bem('copy')">
        It looks like you're on the wrong site for your location.
        <br />
        Would you prefer to shop our {{ expectedLocale?.countryCode?.toUpperCase() }} site?
      </p>

      <div :class="bem('buttons')">
        <div>
          <PrimaryLineLink
            :class="bem('button')"
            size="small"
            is-button
            :disabled="redirecting"
            @click="dismiss"
          >
            Shop {{ $i18n.localeProperties.value.countryCode.toUpperCase() }} site
          </PrimaryLineLink>
        </div>

        <PrimaryButton
          :class="bem('button')"
          size="small"
          :disabled="redirecting"
          @click="changeLocale"
        >
          Shop {{ expectedLocale?.countryCode?.toUpperCase() }} site
        </PrimaryButton>
      </div>
    </div>
  </VisionModal>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { locales } from '~/i18n/constants';

const bem = useBEM('LocaleCheckModal')
const pageStore = usePageStore()
const { localeCheckComplete } = storeToRefs(pageStore)

const showModal = ref(false)
const expectedLocale = ref(null)
const redirecting = ref(false)

async function checkLocale() {
  const { $i18n, $sentry, $track } = useNuxtApp()

  if (localeCheckComplete.value) return

  // Call https://www.sungod.co/cdn-cgi/trace to get the user's location
  try {
    const traceResponse = await $fetch('https://www.sungod.co/cdn-cgi/trace')
    const country = traceResponse?.split('\n')?.find(
      (line) => line.startsWith('loc=')
    ).split('=')?.[1]?.toLowerCase()

    // If the country hasn't been found then bail now
    if (!country) return

    // If the user is on the correct country already then bail now
    if (country === $i18n.localeProperties.value.countryCode) return
    
    // Attempt to determine the correct locale for this user
    expectedLocale.value = locales.find(
      (locale) => locale.countryCode === country
    )

    if (expectedLocale.value) {
      showModal.value = true

      await $track.global.incorrectLocaleModalShown({
        from: $i18n.locale.value,
        to: expectedLocale.value.code,
      })
    }
  } catch (error) {
    $sentry.captureException(error)
  }
}

async function dismiss() {
  if (redirecting.value) return

  pageStore.setLocaleCheckState(true)

  const { $track, $i18n } = useNuxtApp()
  await $track.global.incorrectLocaleModalDismissed({
    from: $i18n.locale.value,
    to: expectedLocale.value.code,
  })

  showModal.value = false
}

async function changeLocale() {
  if (!expectedLocale.value) return
  
  const { $i18n, $track } = useNuxtApp()
  
  await $track.global.incorrectLocaleModalConfirmed({
    from: $i18n.locale.value,
    to: expectedLocale.value.code,
  })

  const url = new URL(window.location.href)

  // Replace the current locale with the new one
  url.pathname = url.pathname.replace(
    new RegExp(`/${$i18n.locale.value}(?=/|$)`),
    `/${expectedLocale.value.code}`
  )

  // Add country query parameter to update locale redirecter
  url.searchParams.set('country', expectedLocale.value.countryCode)

  // Ensure we don't reload the page if the URL is the same
  if (url.href === window.location.href) return await this.dismiss()

  redirecting.value = true
  pageStore.setLocaleCheckState(true)
  window.location.href = url.href
}

checkLocale()
</script>

<style lang="postcss" scoped>
.LocaleCheckModal__inner {
  @apply p-sm
    flex flex-col gap-xs;
}

.LocaleCheckModal__buttons {
  @apply flex gap-md items-center justify-center
    mt-sm smMax:mt-xs;
}
</style>